import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { cn } from "@/lib/utils";
// ✅ Import del logo local
import logoGavillero from "@/assets/images/logo/gavillero-logo.png";

const NAV_LINKS = [
  { label: "Historia", href: "/#historia" },
  { label: "La Propiedad", href: "/#propiedad" },
  { label: "Experiencias", href: "/#experiencias" },
  { label: "Galería", href: "/#galeria" },
  { label: "Eventos", href: "/#eventos" },
  { label: "Contacto", href: "/#contacto" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location]);

  // ✅ Manejar el scroll a la sección cuando se hace clic en un enlace con #
  const handleNavClick = (e, href) => {
    e.preventDefault();
    const targetId = href.replace('/', '').replace('#', '');
    
    // Si estamos en la página principal, hacer scroll a la sección
    if (location.pathname === '/') {
      const element = document.getElementById(targetId);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
        // Actualizar URL sin recargar
        window.history.pushState(null, '', href);
      }
    } else {
      // Si estamos en otra página, navegar a la página principal con el hash
      window.location.href = href;
    }
  };

  const isDark = location.pathname === "/" && !scrolled;

  return (
    <header
      className={cn(
        "fixed top-0 left-0 right-0 z-50 transition-all duration-500",
        scrolled ? "glass-dark py-3" : "bg-transparent py-5"
      )}
    >
      <nav className="max-w-7xl mx-auto px-6 lg:px-10 flex items-center justify-between">
        {/* Logo */}
        <Link to="/" className="flex items-center gap-3 group">
          <img
            src={logoGavillero}
            alt="Casa El Gavillero"
            className="h-9 w-9 rounded-full object-cover ring-1 ring-white/20 transition-transform group-hover:scale-105"
          />
          <span
            className={cn(
              "font-serif-display text-lg tracking-wide transition-colors",
              isDark ? "text-white" : "text-white"
            )}
          >
            Casa El Gavillero
          </span>
        </Link>

        {/* Desktop nav */}
        <div className="hidden lg:flex items-center gap-8">
          {NAV_LINKS.map((link) => (
            <a
              key={link.href}
              href={link.href}
              onClick={(e) => handleNavClick(e, link.href)}
              className={cn(
                "text-sm font-light tracking-wide transition-colors relative group",
                "text-white/80 hover:text-white"
              )}
            >
              {link.label}
              <span className="absolute -bottom-1 left-0 w-0 h-px bg-white/60 transition-all duration-300 group-hover:w-full" />
            </a>
          ))}
        </div>

        {/* Botón de menú móvil */}
        <button
          onClick={() => setOpen(!open)}
          className="lg:hidden text-white p-2"
          aria-label="Menú"
        >
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </nav>

      {/* Mobile menu */}
      {open && (
        <div className="lg:hidden glass-dark mt-3 mx-4 p-6 animate-fade-in">
          <div className="flex flex-col gap-4">
            {NAV_LINKS.map((link) => (
              <a
                key={link.href}
                href={link.href}
                onClick={(e) => handleNavClick(e, link.href)}
                className="text-white/80 hover:text-white text-sm font-light tracking-wide py-2 border-b border-white/10"
              >
                {link.label}
              </a>
            ))}
            <Link
              to="/reservar"
              className="mt-2 inline-flex items-center justify-center px-6 py-3 text-sm font-light border border-white/30 text-white hover:bg-white hover:text-neutral-900 transition-all"
            >
              Reservar
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}